import {
  Drawer,
  Modal,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useRef, useState } from "react";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { RootState } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";
import {
  createJobDescription,
  getAiConfig,
  updateJobDescription,
} from "../../../core/store/reducers/candidate-screening.slice";

const SCORING_MODE_OPTIONS = [
  { value: "weighted_blend", name: "V1" },
  { value: "weighted_blend_v2", name: "V2" },
];

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

const CreateEditJobDescriptionComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const isCopyMode = Boolean(selectedEntity?.isCopy);
  const isEditMode = Boolean(selectedEntity && !isCopyMode);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { aiConfig } = useSelector(
    (state: RootState) => state.candidateScreening
  );
  const [saving, setSaving] = useState(false);
  const [enabledScores, setEnabledScores] = useState({
    rule: false,
    similarity: false,
    ai: true,
  });
  const [selectedScoringMode, setSelectedScoringMode] =
    useState("weighted_blend");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const isInitialDistributionDoneRef = useRef(false);

  const toSkillRows = (skills: any[], defaultWeight: number) => {
    if (!Array.isArray(skills) || skills.length === 0) {
      return defaultWeight > 0 ? [{ value: "", weight: defaultWeight }] : [];
    }
    return skills.flatMap((skill) => {
      const names = String(skill?.value || skill?.name || skill?.skill || "")
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
      if (!names.length) return [];
      const groupWeight = Number(skill?.weight ?? defaultWeight);
      const rowWeight =
        Number.isFinite(groupWeight) && groupWeight > 0
          ? Number((groupWeight / names.length).toFixed(2))
          : 0;
      return names.map((value) => ({ value, weight: rowWeight }));
    });
  };

  const toSkillPayload = (skills: any[]) => {
    if (!Array.isArray(skills)) return [];
    return skills
      .map((skill) => ({
        value: (skill?.value || "").trim(),
        weight: Number(skill?.weight ?? 0),
      }))
      .filter(
        (skill) =>
          skill.value && Number.isFinite(skill.weight) && skill.weight > 0
      );
  };

  const getSkillWeightTotal = (skills: any[] = []) =>
    skills.reduce((sum, skill) => {
      const value = (skill?.value || "").trim();
      const weight = Number(skill?.weight);
      return value && Number.isFinite(weight) && weight > 0
        ? sum + weight
        : sum;
    }, 0);

  const hasIncompleteSkillRows = (skills: any[] = []) =>
    skills.some((skill) => {
      const hasValue = Boolean((skill?.value || "").trim());
      const weight = Number(skill?.weight);
      const hasWeight = Number.isFinite(weight) && weight > 0;
      return hasValue !== hasWeight;
    });

  const hasAnySkill = (skills: any[] = []) =>
    skills.some((skill) => (skill?.value || "").trim());

  const formatWeight = (weight: number) => Number(weight.toFixed(2));

  const getEnabledFromWeights = (weightConfig: any) => ({
    rule: Number(weightConfig?.rule ?? 0) > 0,
    similarity: Number(weightConfig?.similarity ?? 0) > 0,
    ai: Number(weightConfig?.ai ?? 0) > 0,
  });

  const getDefaultScoringMode = () =>
    aiConfig?.scoringMode ||
    (String(aiConfig?.promptVersion || "").toLowerCase() === "v2"
      ? "weighted_blend_v2"
      : "weighted_blend");

  const getScoringStrategyInfo = () =>
    selectedScoringMode === "weighted_blend_v2"
      ? {
          message: "V2 scoring gives baseline credit first.",
          description:
            "When a skill is present in the resume, V2 gives 55% baseline credit. If the resume has stronger project/work evidence, the score increases further.",
        }
      : {
          message: "V1 scoring is stricter.",
          description:
            "V1 expects project/work evidence for each skill. A skill that is only listed may receive a low score because the resume does not prove real usage.",
        };

  const getDistributedWeights = (enabled: any) => {
    const keys = ["rule", "similarity", "ai"].filter((key) => enabled[key]);
    if (!keys.length) return { weightRule: 0, weightSimilarity: 0, weightAi: 0 };

    const base = Math.floor((1 / keys.length) * 100) / 100;
    const last = Number((1 - base * (keys.length - 1)).toFixed(2));
    const values: any = { weightRule: 0, weightSimilarity: 0, weightAi: 0 };
    keys.forEach((key, index) => {
      const weight = index === keys.length - 1 ? last : base;
      if (key === "rule") values.weightRule = weight;
      if (key === "similarity") values.weightSimilarity = weight;
      if (key === "ai") values.weightAi = weight;
    });
    return values;
  };

  const applyScoreToggles = (nextEnabled: any) => {
    if (!nextEnabled.rule && !nextEnabled.similarity && !nextEnabled.ai) {
      showNotification("At least one score component must be enabled.", "warning");
      form.setFieldsValue({
        enableRuleScore: enabledScores.rule,
        enableSimilarityScore: enabledScores.similarity,
        enableAiScore: enabledScores.ai,
      });
      return;
    }
    setEnabledScores(nextEnabled);
    form.setFieldsValue({
      enableRuleScore: nextEnabled.rule,
      enableSimilarityScore: nextEnabled.similarity,
      enableAiScore: nextEnabled.ai,
      ...getDistributedWeights(nextEnabled),
    });
  };

  const getSkillWeightTotals = (values: any) => ({
    primary: formatWeight(getSkillWeightTotal(values?.primaryRules)),
    secondary: formatWeight(getSkillWeightTotal(values?.secondaryRules)),
  });

  useEffect(() => {
    if (!aiConfig) {
      dispatch(getAiConfig());
    }
  }, [aiConfig, dispatch]);

  useEffect(() => {
    if (selectedEntity) {
      const nextEnabled = getEnabledFromWeights(selectedEntity?.weightConfig);
      const nextScoringMode =
        selectedEntity?.scoringMode ?? getDefaultScoringMode();
      setEnabledScores(nextEnabled);
      setSelectedScoringMode(nextScoringMode);
      form.setFieldsValue({
        title: selectedEntity?.title,
        status: selectedEntity?.status,
        rawText: selectedEntity?.rawText,
        enableRuleScore: nextEnabled.rule,
        enableSimilarityScore: nextEnabled.similarity,
        enableAiScore: nextEnabled.ai,
        scoringMode: nextScoringMode,
        weightSimilarity: selectedEntity?.weightConfig?.similarity,
        weightRule: selectedEntity?.weightConfig?.rule,
        weightAi: selectedEntity?.weightConfig?.ai,
        primaryRules: toSkillRows(selectedEntity?.primarySkills, 1),
        secondaryRules: toSkillRows(selectedEntity?.secondarySkills, 0),
      });
      isInitialDistributionDoneRef.current = true;
    } else {
      // For new entities, explicitly clear the fields, especially weights
      form.resetFields();
      setEnabledScores({ rule: false, similarity: false, ai: true });
      setSelectedScoringMode(getDefaultScoringMode());
      isInitialDistributionDoneRef.current = false;
    }
  }, [form, selectedEntity, aiConfig]);

  const getEnabledFromFormValues = (values: any) => ({
    rule: !!values?.enableRuleScore,
    similarity: !!values?.enableSimilarityScore,
    ai: !!values?.enableAiScore,
  });

  const normalizeText = (value: any) =>
    String(value || "")
      .replace(/\s+/g, " ")
      .trim();

  const formatScoreWeight = (value: any) =>
    `${Math.round(Number(value || 0) * 100)}%`;

  const scrollToScoreWeights = () => {
    setTimeout(() => {
      document
        .getElementById("jd-score-weights-anchor")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const submitJobDescription = (payload: any) => {
    setSaving(true);
    const action = isEditMode
      ? updateJobDescription({ id: selectedEntity?.id, body: payload })
      : createJobDescription(payload);

    dispatch(action)
      .then((val: any) => {
        if (val?.payload?.data) {
          showNotification(
            isEditMode
              ? "Job Description Updated Successfully"
              : "Job Description Created Successfully",
            "success"
          );
          setConfirmOpen(false);
          setPendingPayload(null);
          onSubmit();
        }
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const buildPayload = (values: any) => {
    const selectedScores = getEnabledFromFormValues(values);
    return {
      title: values?.title,
      status: values?.status,
      rawText: values?.rawText,
      scoringMode: values?.scoringMode ?? getDefaultScoringMode(),
      weightConfig: {
        similarity: selectedScores.similarity ? Number(values?.weightSimilarity ?? 0) : 0,
        rule: selectedScores.rule ? Number(values?.weightRule ?? 0) : 0,
        ai: selectedScores.ai ? Number(values?.weightAi ?? 0) : 0,
      },
      primarySkills: selectedScores.rule ? toSkillPayload(values?.primaryRules) : [],
      secondarySkills: selectedScores.rule ? toSkillPayload(values?.secondaryRules) : [],
    };
  };

  const onFinish = (values: any) => {
    if (
      isCopyMode &&
      normalizeText(values?.title) === normalizeText(selectedEntity?.title) &&
      normalizeText(values?.rawText) === normalizeText(selectedEntity?.rawText)
    ) {
      showNotification(
        "Please update the job title or job description before saving the cloned JD.",
        "warning"
      );
      return;
    }

    const selectedScores = getEnabledFromFormValues(values);
    const total =
      (selectedScores.similarity ? Number(values?.weightSimilarity ?? 0) : 0) +
      (selectedScores.rule ? Number(values?.weightRule ?? 0) : 0) +
      (selectedScores.ai ? Number(values?.weightAi ?? 0) : 0);
    // Weights blend the final score (plan.md §5). They must sum to 1.

    if (Math.abs(total - 1) > 0.001) {
      showNotification(
        "Score weights (similarity + rule + AI) must add up to 1.",
        "warning"
      );
      return;
    }

    if (selectedScores.rule && Number(values?.weightRule ?? 0) > 0) {
      if (!hasAnySkill(values?.primaryRules)) {
        showNotification(
          "Primary skills are required when Rule Weight is greater than 0.",
          "warning"
        );
        return;
      }
    }

    if (
      selectedScores.rule &&
      (hasIncompleteSkillRows(values?.primaryRules) ||
        hasIncompleteSkillRows(values?.secondaryRules))
    ) {
      showNotification("Each configured skill must have a skill name and weight.", "warning");
      return;
    }

    const skillWeightTotals = selectedScores.rule
      ? getSkillWeightTotals(values)
      : { primary: 0, secondary: 0 };
    if (
      selectedScores.rule &&
      (skillWeightTotals.primary > 1 || skillWeightTotals.secondary > 1)
    ) {
      showNotification(
        "Primary and secondary skill weights must each not exceed 1.00.",
        "warning"
      );
      return;
    }

    const payload = buildPayload(values);
    setPendingPayload(payload);
    setConfirmOpen(true);
  };

  const colSpan = screenType.isPortrait ? 24 : 12;
  const isScreeningLocked = Boolean(
    isEditMode &&
      (selectedEntity?.hasScreeningResults ||
        Number(selectedEntity?.candidateCount || 0) > 0)
  );
  const isOnlyAiScoreEnabled =
    enabledScores.ai && !enabledScores.rule && !enabledScores.similarity;

  const formProps = {
    form: form,
    name: "create-edit-job-description",
    heading: isCopyMode
      ? "Clone JD"
      : isEditMode
      ? "Edit Job Description"
      : "Add New Job Description",
    initialValues: {
      status: "ACTIVE",
      enableRuleScore: false,
      enableSimilarityScore: false,
      enableAiScore: true,
      scoringMode: getDefaultScoringMode(),
      primaryRules: [{ value: "", weight: 1 }],
      secondaryRules: [],
      weightRule: 0,
      weightSimilarity: 0,
      weightAi: 1,
    },
    formElements: [
      {
        name: "title",
        colSpan: 24,
        label: "Job Title",
        placeholder: "e.g. Senior Backend Engineer",
        type: "Input",
        allowClear: true,
        disabled: isScreeningLocked,
        rules: [{ required: true, message: "Please enter a job title" }],
      },
      {
        name: "status",
        label: "Status",
        placeholder: "Status",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
        disabled: isScreeningLocked,
        rules: [{ required: true, message: "Please select a status" }],
      },
      {
        name: "rawText",
        colSpan: 24,
        label: "Job Description Text",
        placeholder:
          "Paste the full job description. This is parsed and embedded for screening.",
        type: "TextArea",
        rows: 8,
        showCount: true,
        maxLength: 20000,
        disabled: isScreeningLocked,
        rules: [
          { required: true, message: "Please enter the job description text" },
        ],
      },
      {
        type: "InfoAlert",
        colSpan: 24,
        name: "screeningLockNotice",
        alertType: "info",
        visible: isScreeningLocked,
        message: "Job description content is locked.",
        description:
          "This JD already has screening results. You can change score component toggles/weights for future screening, but JD text and skills are preserved.",
      },
      {
        type: "TextLabel",
        colSpan: 24,
        name: "scoreComponentHeading",
        textlabel: "Score Components",
        tooltip:
          "Turn off a scoring component if it should not contribute to the candidate final score.",
      },
      {
        name: "enableAiScore",
        label: "AI Score",
        type: "Switch",
        valuePropName: "checked",
        colSpan: 8,
      },
      // {
      //   name: "enableRuleScore",
      //   label: "Rule Weight Score",
      //   type: "Switch",
      //   valuePropName: "checked",
      //   colSpan: 8,
      // },
      // {
      //   name: "enableSimilarityScore",
      //   label: "Similarity Score",
      //   type: "Switch",
      //   valuePropName: "checked",
      //   colSpan: 8,
      // },
      {
        name: "scoringMode",
        label: "AI Scoring Strategy",
        placeholder: "Select scoring strategy",
        type: "Radio",
        optionType: "button",
        colSpan: 24,
        options: SCORING_MODE_OPTIONS,
        visible: enabledScores.ai,
      },
      {
        type: "InfoAlert",
        colSpan: 24,
        name: "scoringStrategyHelp",
        alertType: "info",
        visible: enabledScores.ai,
        message: getScoringStrategyInfo().message,
        description: getScoringStrategyInfo().description,
      },
      {
        type: "TextLabel",
        colSpan: 24,
        name: "primarySkillsLabel",
        textlabel: "Rule Weight Strategy",
        visible: enabledScores.rule,
      },
      {
        type: "InfoAlert",
        colSpan: 24,
        name: "scoreWeightsHelp",
        alertType: "warning",
        message: "Rule Weight uses the skills below.",
        visible: enabledScores.rule,
        description: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Each skill can have its own weight.</li>
            <li>Primary Skills are mandatory when Rule Weight is greater than 0.</li>
            <li>Secondary Skills are optional.</li>
            <li>Total skill weight must not exceed 1.00.</li>
          </ul>
        ),
      },
      {
        type: "PrimaryRule",
        name: "primaryRules",
        colSpan: 24,
        visible: enabledScores.rule,
        disabled: isScreeningLocked,
        
      },
      {
        type: "SecondaryRule",
        name: "secondaryRules",
        colSpan: 24,
        visible: enabledScores.rule,
        disabled: isScreeningLocked,
      },
      {
        type: "Custom",
        colSpan: 24,
        name: "scoreWeightsAnchor",
        render: () => <div id="jd-score-weights-anchor" />,
      },
      {
        type: "TextLabel",
        colSpan: 24,
        name: "scoreWeightsHeading",
        textlabel: "Score Weights",
        tooltip:
          "These three weights decide how much each scoring component contributes to the final candidate score. The total must equal 1.00.",
      },
      {
        name: "weightAi",
        label: "AI Weight",
        tooltip:
          "Uses AI evidence scoring for the configured skills. Higher AI weight means the AI evidence score affects the final score more.",
        placeholder: "0.30",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 100,
        isWeight: true,
        isDecimal: true,
        visible: enabledScores.ai,
        disabled: isOnlyAiScoreEnabled,
       
      },
      {
        name: "weightRule",
        label: "Rule Weight",
        tooltip:
          "Uses the primary and secondary skills configured above. Higher rule weight means skill matches affect the final score more.",
        placeholder: "0.20",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 100,
        isWeight: true,
        isDecimal: true,
        visible: enabledScores.rule,
       
      },
      {
        name: "weightSimilarity",
        label: "Similarity Weight",
        tooltip:
          "Compares the full job description and resume as embeddings. Higher similarity weight means overall resume/JD semantic match affects the final score more.",
        placeholder: "0.50",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 100,
        isWeight: true,
        isDecimal: true,
        visible: enabledScores.similarity,
        
      }
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      ...(!isScreeningLocked
        ? [{
            htmlType: "button",
            colSpan: 5,
            loading: false,
            submitButtonText: "Clear",
            type: "clearForm", // This is handled by GenericFormComponent
            onClick: () => {
              // Reset the flag when the form is cleared
              setEnabledScores({ rule: false, similarity: false, ai: true });
              isInitialDistributionDoneRef.current = false;
            },
          }]
        : []),
      {
        htmlType: "submit",
        colSpan: 5,
        loading: saving,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFinish,
    onFinishFailed: () => {
      return;
    },
   onValuesChange: (changedValues: any, allValues: any) => {
  if (Object.prototype.hasOwnProperty.call(changedValues, "scoringMode")) {
    setSelectedScoringMode(changedValues.scoringMode || "weighted_blend");
  }

  if (Object.prototype.hasOwnProperty.call(changedValues, "enableRuleScore")) {
    applyScoreToggles({
      ...enabledScores,
      rule: !!changedValues.enableRuleScore,
    });
    return;
  }
  if (Object.prototype.hasOwnProperty.call(changedValues, "enableSimilarityScore")) {
    applyScoreToggles({
      ...enabledScores,
      similarity: !!changedValues.enableSimilarityScore,
    });
    return;
  }
  if (Object.prototype.hasOwnProperty.call(changedValues, "enableAiScore")) {
    applyScoreToggles({
      ...enabledScores,
      ai: !!changedValues.enableAiScore,
    });
    return;
  }

  const changedWeightField = Object.keys(changedValues).find(
    (key) =>
      key === "weightSimilarity" ||
      key === "weightRule" ||
      key === "weightAi"
  );

  if (!changedWeightField) return;

  const toDecimal = (value: any) => {
    const num = Number(value);

    if (Number.isNaN(num) || num < 0) {
      return 0;
    }

    return num > 1 ? num / 100 : num;
  };

  const finalSim = enabledScores.similarity ? toDecimal(allValues.weightSimilarity) : 0;
  const finalRule = enabledScores.rule ? toDecimal(allValues.weightRule) : 0;
  const finalAi = enabledScores.ai ? toDecimal(allValues.weightAi) : 0;

  const total = Number(
    (finalSim + finalRule + finalAi).toFixed(2)
  );

  if (Math.abs(total - 1) > 0.001) {
    form.setFields([
      {
        name: "weightAi",
        errors: ["Total weight must equal 1.00"],
      },
    ]);
  } else {
    form.setFields([
      {
        name: "weightAi",
        errors: [],
      },
    ]);
  }
},
  };

  

  return (
    <Drawer
      maskClosable={false}
      title={<strong>{formProps.heading}</strong>}
      className="form-drawer"
      width={
        screenType.isPortrait
          ? "90%"
          : screenType.isLg || screenType.isXl
          ? 720
          : 500
      }
      onClose={onClose}
      open={visible}
    >
      <GenericFormComponent {...formProps} />
      <Modal
        title="Confirm Score Weights"
        open={confirmOpen}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={saving}
        onOk={() => submitJobDescription(pendingPayload)}
        onCancel={() => {
          setConfirmOpen(false);
          scrollToScoreWeights();
        }}
      >
        <p>Please confirm the score weights before saving this job description.</p>
        <div style={{ display: "grid", gap: 8 }}>
          {Number(pendingPayload?.weightConfig?.ai || 0) > 0 && (
            <div>
              <strong>AI Score:</strong>{" "}
              {formatScoreWeight(pendingPayload?.weightConfig?.ai)}
            </div>
          )}
          {Number(pendingPayload?.weightConfig?.rule || 0) > 0 && (
            <div>
              <strong>Rule Weight Score:</strong>{" "}
              {formatScoreWeight(pendingPayload?.weightConfig?.rule)}
            </div>
          )}
          {Number(pendingPayload?.weightConfig?.similarity || 0) > 0 && (
            <div>
              <strong>Similarity Score:</strong>{" "}
              {formatScoreWeight(pendingPayload?.weightConfig?.similarity)}
            </div>
          )}
        </div>
      </Modal>
     
    </Drawer>
  );
};

export default CreateEditJobDescriptionComponent;
